import { useEffect } from 'react'
import { NumberInput, Text, TextInput } from '@mantine/core'
import type { TaskArgsComponentProps } from '../args-registry'
import { buildNotifyArgs, extractNewDocs, extractWebhookUrl } from './NotifyArgs.utils'

/**
 * Args editor/viewer for cpg.notify (CPG Notifications): { new_docs?, slack_webhook_url? }.
 * Both empty is the daily case — post everything in CPG that needs attention (documents
 * waiting for approval, new measure proposals; nothing when there is none). new_docs is
 * normally filled by the cpg.classify followup chain.
 */
export function NotifyArgs({ value, onChange, readOnly }: TaskArgsComponentProps) {
  const newDocs = extractNewDocs(value)
  const webhookUrl = extractWebhookUrl(value)

  // Edit mode opened with no args: seed the empty-object default so the dialog opens
  // valid and the posted run carries explicit args.
  useEffect(() => {
    if (!readOnly && (value === undefined || value === null)) {
      onChange?.({})
    }
  }, [readOnly, value, onChange])

  if (readOnly) {
    return (
      <Text size="sm">
        {typeof newDocs === 'number' ? (
          <>
            <b>{newDocs}</b> newly classified document(s) + everything else that needs attention
          </>
        ) : (
          <>
            <b>Daily notification</b> — everything in CPG that needs attention (default)
          </>
        )}
        {webhookUrl !== undefined ? ' · webhook override from args' : ' · environment webhook'}
      </Text>
    )
  }

  const webhookText = typeof webhookUrl === 'string' ? webhookUrl : ''
  return (
    <div className="flex flex-col gap-2">
      <NumberInput
        label="New documents"
        description="How many documents were just classified (normally set by the cpg.classify followup). Leave empty for the daily notification."
        placeholder="daily notification"
        min={1}
        step={1}
        allowDecimal={false}
        value={typeof newDocs === 'number' ? newDocs : ''}
        onChange={(next) =>
          onChange?.(buildNotifyArgs(typeof next === 'number' ? next : undefined, webhookText))
        }
      />
      <TextInput
        label="Slack webhook URL"
        description="Overrides the environment's CPG_NOTIFICATION_SLACK_WEBHOOK_URL for this run. Leave empty to use the environment webhook."
        placeholder="environment webhook"
        value={webhookText}
        onChange={(event) =>
          onChange?.(
            buildNotifyArgs(
              typeof newDocs === 'number' ? newDocs : undefined,
              event.currentTarget.value,
            ),
          )
        }
      />
    </div>
  )
}
